import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Download, Loader2, Zap, Users, TrendingUp, Target, ArrowUp, ArrowDown, Minus } from 'lucide-react';

function exportCSV(filename, headers, rows) {
  const csvContent = [headers.join(','), ...rows.map(r => headers.map(h => `"${r[h] ?? ''}"`).join(','))].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
}

const Stat = ({ label, value, icon: Icon, accent, warn }) => (
  <div className="p-3 bg-zinc-950/50 border border-zinc-800 rounded-sm text-center" data-testid={`stat-${label?.toLowerCase().replace(/\s+/g, '-')}`}>
    {Icon && <Icon className={`w-4 h-4 mx-auto mb-1 ${accent ? 'text-amber-400' : warn ? 'text-rose-400' : 'text-zinc-500'}`} />}
    <div className={`text-xl font-bold font-mono ${accent ? 'text-amber-400' : 'text-zinc-100'}`}>{value ?? '—'}</div>
    <div className="text-[11px] text-zinc-500 mt-0.5">{label}</div>
  </div>
);

const Delta = ({ delta }) => {
  if (!delta) return null;
  const dir = delta.direction;
  const Icon = dir === 'improved' ? ArrowUp : dir === 'declined' ? ArrowDown : Minus;
  const color = dir === 'improved' ? 'text-emerald-400' : dir === 'declined' ? 'text-rose-400' : 'text-zinc-500';
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-mono ${color}`}>
      <Icon className="w-2.5 h-2.5" />
      {delta.pct != null ? `${delta.pct > 0 ? '+' : ''}${delta.pct}%` : ''}
    </span>
  );
};

export function VBTTestingReport({ dateFrom, dateTo, teamId, athleteId }) {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (teamId) params.append('team_id', teamId);
      if (athleteId) params.append('athlete_id', athleteId);
      const res = await api.get(`/reports/vbt?${params}`);
      setData(res.data);
    } catch { toast.error('Failed to load VBT report'); }
    finally { setLoading(false); }
  }, [api, dateFrom, dateTo, teamId, athleteId]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>;
  if (!data) return <div className="text-center py-12 text-zinc-500 text-sm">Select filters and date range to generate report</div>;

  const s = data.summary;
  const csvRows = data.tests.map(t => ({
    athlete_name: t.athlete_name, team: t.team, test_date: t.test_date,
    exercise: t.exercise, f0: t.f0, v0: t.v0, pmax: t.pmax, r_squared: t.r_squared,
    delta_v0_pct: t.delta?.v0?.pct ?? '', delta_pmax_pct: t.delta?.pmax?.pct ?? '',
  }));

  return (
    <div className="space-y-5" data-testid="vbt-testing-report">
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Total Tests" value={s.total_tests} icon={Zap} />
        <Stat label="Athletes" value={s.unique_athletes} icon={Users} />
        <Stat label="Avg V0" value={s.avg_v0 ? `${s.avg_v0} m/s` : '—'} accent icon={TrendingUp} />
        <Stat label="Avg Pmax" value={s.avg_pmax ? `${s.avg_pmax} W` : '—'} accent icon={Target} />
      </div>

      {/* Export */}
      {data.tests.length > 0 && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-400 h-8 text-xs"
            data-testid="export-vbt"
            onClick={() => exportCSV('vbt_testing_report.csv',
              ['athlete_name', 'team', 'test_date', 'exercise', 'f0', 'v0', 'pmax', 'r_squared', 'delta_v0_pct', 'delta_pmax_pct'],
              csvRows
            )}>
            <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
          </Button>
        </div>
      )}

      {/* Desktop table */}
      <div className="overflow-x-auto hidden sm:block">
        <table className="w-full text-sm" data-testid="vbt-table">
          <thead>
            <tr className="border-b border-zinc-800 text-zinc-500 text-xs">
              <th className="text-left py-2 px-3 font-medium">Athlete</th>
              <th className="text-left py-2 px-2 font-medium">Team</th>
              <th className="text-center py-2 px-2 font-medium">Date</th>
              <th className="text-center py-2 px-2 font-medium">Exercise</th>
              <th className="text-right py-2 px-2 font-medium">F0 (kg)</th>
              <th className="text-right py-2 px-2 font-medium">V0 (m/s)</th>
              <th className="text-right py-2 px-2 font-medium">Pmax (W)</th>
              <th className="text-center py-2 px-2 font-medium">R²</th>
              <th className="text-center py-2 px-3 font-medium">vs Prev</th>
            </tr>
          </thead>
          <tbody>
            {data.tests.map(t => (
              <tr key={t.test_id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors">
                <td className="py-2.5 px-3 text-zinc-200 font-medium">{t.athlete_name}</td>
                <td className="py-2.5 px-2 text-zinc-500 text-xs">{t.team}</td>
                <td className="py-2.5 px-2 text-center text-zinc-400 text-xs font-mono">{t.test_date}</td>
                <td className="py-2.5 px-2 text-center">
                  <Badge variant="outline" className="text-[10px] border-blue-500/30 text-blue-400">{t.exercise}</Badge>
                </td>
                <td className="py-2.5 px-2 text-right text-zinc-300 font-mono text-xs">
                  {t.f0 ?? '—'}
                  {t.delta?.l0 && <span className="ml-1"><Delta delta={t.delta.l0} /></span>}
                </td>
                <td className="py-2.5 px-2 text-right text-zinc-300 font-mono text-xs">
                  {t.v0 ?? '—'}
                  {t.delta?.v0 && <span className="ml-1"><Delta delta={t.delta.v0} /></span>}
                </td>
                <td className="py-2.5 px-2 text-right font-mono text-xs font-bold text-amber-400">
                  {t.pmax ?? '—'}
                  {t.delta?.pmax && <span className="ml-1"><Delta delta={t.delta.pmax} /></span>}
                </td>
                <td className="py-2.5 px-2 text-center">
                  {t.r_squared != null ? (
                    <span className={`text-xs font-mono ${t.r_squared >= 0.95 ? 'text-emerald-400' : t.r_squared >= 0.85 ? 'text-amber-400' : 'text-rose-400'}`}>
                      {t.r_squared}
                    </span>
                  ) : '—'}
                </td>
                <td className="py-2.5 px-3 text-center text-[10px] text-zinc-500">
                  {t.delta?.prev_date ? t.delta.prev_date : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="sm:hidden space-y-2">
        {data.tests.map(t => (
          <div key={`m-${t.test_id}`} className="p-3 bg-zinc-900/50 border border-zinc-800 rounded-sm">
            <div className="flex justify-between items-start mb-2">
              <div>
                <span className="text-sm text-zinc-200 font-medium block">{t.athlete_name}</span>
                <span className="text-[10px] text-zinc-500">{t.team} · {t.test_date}</span>
              </div>
              <Badge variant="outline" className="text-[10px] border-blue-500/30 text-blue-400">{t.exercise}</Badge>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-[10px] text-zinc-500">F0</div>
                <div className="text-xs text-zinc-300 font-mono">{t.f0 ?? '—'} <Delta delta={t.delta?.l0} /></div>
              </div>
              <div>
                <div className="text-[10px] text-zinc-500">V0</div>
                <div className="text-xs text-zinc-300 font-mono">{t.v0 ?? '—'} <Delta delta={t.delta?.v0} /></div>
              </div>
              <div>
                <div className="text-[10px] text-zinc-500">Pmax</div>
                <div className="text-xs text-amber-400 font-mono font-bold">{t.pmax ?? '—'} <Delta delta={t.delta?.pmax} /></div>
              </div>
            </div>
            {t.r_squared != null && (
              <div className="mt-1.5 text-[10px] text-zinc-500">
                R² = <span className={t.r_squared >= 0.95 ? 'text-emerald-400' : 'text-amber-400'}>{t.r_squared}</span>
                {t.delta?.prev_date && <span className="ml-2">vs {t.delta.prev_date}</span>}
              </div>
            )}
          </div>
        ))}
      </div>

      {data.tests.length === 0 && <div className="text-center py-8 text-zinc-500 text-sm">No VBT tests found in this period</div>}
    </div>
  );
}

export default VBTTestingReport;
